import type {
  CustomFieldsListResponse,
  CustomFormFieldsParams,
  CustomFormFieldsResponse,
  CreateCustomFieldPayload,
  UpdateCustomFieldPayload,
  UpdateOfferSectionsPayload,
  CustomFieldResponse,
  UpdateFormFieldsPayload,
  OfferTemplateTokenWarning,
} from './customFields.interface';
import { http } from '@/lib/ats-https';

// A refused write resolves with `errors` as a list or a keyed object (see the RTK base query).
const hasErrors = (errors: unknown) =>
  Array.isArray(errors)
    ? errors.length > 0
    : Boolean(errors && Object.keys(errors).length > 0);

export const customFieldsApi = {
  // Settings list for a subject: custom fields and form template.
  getFields: async (subjectType: string): Promise<CustomFieldsListResponse> => {
    const response = await http.get(
      `/ats_custom_fields?subject_type=${subjectType}`
    );
    return response.data;
  },

  // Fields a record form renders, scoped by department/office.
  getFormFields: async <TField>(
    params: CustomFormFieldsParams
  ): Promise<CustomFormFieldsResponse<TField>> => {
    const response = await http.get('/ats_custom_fields/form_fields', {
      params,
    });
    const body = response as unknown as {
      data?: CustomFormFieldsResponse<TField>;
      errors?: unknown;
    };
    if (hasErrors(body?.errors)) {
      throw { errors: body.errors };
    }
    return body?.data ?? {};
  },

  // Create a new custom field
  createCustomField: async (
    payload: CreateCustomFieldPayload
  ): Promise<CustomFieldResponse> => {
    const response = await http.post('/ats_custom_fields', {
      ats_custom_field: payload,
    });
    // ats-https interceptor already unwraps to response body; no `.data` here
    const body = response as unknown as CustomFieldResponse;
    if (hasErrors(body?.errors)) {
      throw { errors: body.errors };
    }
    return body;
  },

  // Update an existing custom field
  updateCustomField: async (
    payload: UpdateCustomFieldPayload | UpdateOfferSectionsPayload
  ): Promise<CustomFieldResponse> => {
    const { id, ...updateData } = payload;
    const response = await http.put(`/ats_custom_fields/${id}`, {
      ats_custom_field: updateData,
    });
    // ats-https interceptor already unwraps to response body; no `.data` here
    const body = response as unknown as CustomFieldResponse;
    if (hasErrors(body?.errors)) {
      throw { errors: body.errors };
    }
    return body;
  },

  // Delete a custom field
  deleteCustomField: async (fieldId: number): Promise<void> => {
    const response = await http.delete(`/ats_custom_fields/${fieldId}`);
    // The ats-https interceptor RESOLVES 4xx responses with the body (see
    // auth-interceptor), so a blocked delete — e.g. a field used as an approval
    // reviewer returns `err_field_used_in_approval` at 422 — comes back resolved
    // with an `errors` array. Surface it as a rejection so the mutation hits
    // onError (and the UI is not optimistically updated as if it succeeded).
    const body = response as unknown as { errors?: string[] } | undefined;
    if (hasErrors(body?.errors)) {
      throw { errors: body.errors };
    }
  },

  // Update form fields (for default fields)
  updateFormFields: async (
    payload: UpdateFormFieldsPayload
  ): Promise<CustomFieldsListResponse> => {
    const subjectType = payload.subject_type ?? 'requisition';
    const response = await http.post(
      `/ats_custom_fields/update_form_fields?subject_type=${subjectType}`,
      payload
    );
    // Same interceptor caveat as deleteCustomField: a 4xx RESOLVES with the
    // body, so a rejected write reaches onSuccess and the row keeps showing a
    // status the server never saved. The rollout window makes this reachable —
    // with the FE flag on and t_candidate_job_field_reorder still off, every
    // job/candidate write here answers 400 `bad_request`.
    const body = response as unknown as CustomFieldsListResponse & {
      errors?: string[];
    };
    if (hasErrors(body?.errors)) {
      throw { errors: body.errors };
    }
    return body;
  },

  // Returns the offer templates whose tokens a save would break; empty when safe.
  validateOfferFormFields: async (
    payload: UpdateFormFieldsPayload
  ): Promise<OfferTemplateTokenWarning[]> => {
    const subjectType = payload.subject_type ?? 'ats_offer';
    const response = await http.post(
      `/ats_custom_fields/validate_offer_form_fields?subject_type=${subjectType}`,
      payload
    );
    const body = response as unknown as {
      data?: OfferTemplateTokenWarning[];
      errors?: string[];
    };
    if (hasErrors(body?.errors)) {
      throw { errors: body.errors };
    }
    return body?.data ?? [];
  },
};
